import { useState } from 'react';
import { Usuario } from '@/auth/types';
import { UseUserManagementReturn, CreateUserData, PasswordChangeData, UpdateUserProfileData, UserRole, UserPasswordViewData } from '../types';
import { useAlert } from '../../../../hooks/useAlert';
import UserService from '../services/userService';

export const useUserManagement = (
  fetchDataUsers: () => void
): UseUserManagementReturn => {
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>({});
  const { errorAlert, successAlert } = useAlert();

  const setLoading = (key: string, loading: boolean) => {
    setLoadingActions(prev => ({ ...prev, [key]: loading }));
  };

  const getUserRoles = (user: Usuario): string[] => {
    if (user.roles && user.roles.length > 0) {
      return user.roles.map(role => {
        if (typeof role === 'object' && (role as UserRole).name) {
          return (role as UserRole).name;
        }
        if (typeof role === 'string') {
          return role;
        }
        return '';
      }).filter(Boolean);
    }
    return [];
  };

  const formatUserRoles = (user: Usuario): string => {
    const roles = getUserRoles(user);
    if (roles.length === 0) return 'Sin roles';
    
    const roleLabels: Record<string, string> = {
      'super_admin': 'Super Admin',
      'tramitadora': 'Tramitadora',
      'contador': 'Contador',
      'escribano_externo': 'Escribano Externo',
      'vendedor': 'Vendedor',
      'comprador': 'Comprador',
    };

    return roles.map(role => roleLabels[role] || role).join(', ');
  };

  const handleSaveRoles = async (userId: number, roles: string[]): Promise<void> => {
    const loadingKey = `roles-${userId}`;
    setLoading(loadingKey, true);

    try {
      const response = await UserService.syncRoles(userId, roles);
      successAlert({ message: response.message });
      fetchDataUsers(); // Refresh the user list
    } catch (error: any) {
      errorAlert({
        title: 'Error al actualizar roles',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const handleChangePassword = async (userId: number, passwordData: PasswordChangeData): Promise<void> => {
    const loadingKey = `password-${userId}`;
    setLoading(loadingKey, true);

    try {
      const response = await UserService.changePassword(userId, passwordData);
      successAlert({ message: response.message });
      fetchDataUsers(); // Refresh the user list
    } catch (error: any) {
      errorAlert({
        title: 'Error al cambiar contraseña',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const handleViewPassword = async (user: Usuario): Promise<UserPasswordViewData> => {
    const loadingKey = `view-password-${user.id}`;
    setLoading(loadingKey, true);

    try {
      return await UserService.getVisiblePassword(user.id);
    } catch (error: any) {
      errorAlert({
        title: 'Error al obtener contraseña',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const handleCopyPassword = async (password: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(password);
      successAlert({ message: 'Contraseña copiada al portapapeles' });
    } catch (error: any) {
      errorAlert({
        title: 'Error al copiar contraseña',
        message: error.message || 'No se pudo copiar la contraseña'
      });
      throw error;
    }
  };

  const handleNotifyCredentials = async (user: Usuario): Promise<void> => {
    const loadingKey = `notify-${user.id}`;
    setLoading(loadingKey, true);

    try {
      const response = await UserService.notifyCredentials(user.email);
      successAlert({ message: response.message });
    } catch (error: any) {
      errorAlert({
        title: 'Error al enviar credenciales',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const handleUpdateProfile = async (userId: number, profileData: UpdateUserProfileData): Promise<void> => {
    const loadingKey = `profile-${userId}`;
    setLoading(loadingKey, true);

    try {
      const response = await UserService.updateUserProfile(userId, profileData);
      successAlert({ message: response.message });
      fetchDataUsers();
    } catch (error: any) {
      errorAlert({
        title: 'Error al actualizar perfil',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const handleCreateUser = async (userData: CreateUserData): Promise<void> => {
    const loadingKey = 'create-user';
    setLoading(loadingKey, true);

    try {
      const response = await UserService.createUser(userData);
      successAlert({ message: response.message });
      fetchDataUsers(); // Refresh the user list
    } catch (error: any) {
      errorAlert({
        title: 'Error al crear usuario',
        message: error.message || 'Error desconocido'
      });
      throw error;
    } finally {
      setLoading(loadingKey, false);
    }
  };

  return {
    handleSaveRoles,
    handleChangePassword,
    handleViewPassword,
    handleCopyPassword,
    handleNotifyCredentials,
    handleUpdateProfile,
    handleCreateUser,
    loadingActions,
    getUserRoles,
    formatUserRoles,
  };
};
